import type { HttpApiError } from '@backmarket/http-api'
import { postBuybackQuotes } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes'
import type { PostBuyBackQuoteListing } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes.types'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import translations from './useUpdateListings.translations'

export async function useUpdateListings({
  listings,
  productId,
  marketCode,
}: {
  listings: PostBuyBackQuoteListing[]
  productId: string
  marketCode: string
}): Promise<{
  shouldFetch: boolean
}> {
  const logger = useLogger()
  const i18n = useI18n()
  const { openErrorToast } = useTheToast()

  try {
    const response = await $httpFetch(postBuybackQuotes, {
      body: {
        productId,
        marketCode,
        listings,
      },
    })

    // 207 multiple responses
    if (response?.length) {
      const failedListings = response.filter(
        (listing) => listing.status !== 202,
      )

      if (failedListings.length) {
        openErrorToast({
          content: i18n(translations.toastUpdateListingsErrorContent),
          duration: 3000,
        })

        /* If all listings failed */
        if (failedListings.length === response.length) {
          /* No need to fetch quotes as all listings update has failed */
          return {
            shouldFetch: false,
          }
        }
      }
    }
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error updating quotes', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })

    /* No need to fetch quotes as listings update failed */
    return {
      shouldFetch: false,
    }
  }

  /* Fetch quotes to update successfully updated listings */
  return {
    shouldFetch: true,
  }
}
